import api from "../api/config";
import { ENDPOINTS } from "../api/endpoints";

/**
 * Get all roles with their permissions
 * @returns {Promise<{success: boolean, roles: Array, count: number} | null>}
 */
export const getAllRoles = async () => {
  try {
    const { data } = await api.get(ENDPOINTS.GET_ALL_ROLES);

    if (!data.success) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    return null;
  }
};

/**
 * Get a specific role by ID
 * @param {number} id - Role ID
 * @returns {Promise<{success: boolean, role: Object} | null>}
 */
export const getRoleById = async (id) => {
  try {
    const { data } = await api.get(ENDPOINTS.GET_ROLE_BY_ID(id));

    if (!data.success) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching role:', error);
    return null;
  }
};

/**
 * Create a new role
 * @param {Object} roleData - Role data
 * @param {string} roleData.name - Role name
 * @param {Object} roleData.permissions - Permissions object (JSON)
 * @returns {Promise<{success: boolean, role: Object, message: string} | null>}
 */
export const createRole = async (roleData) => {
  try {
    // Validate required fields
    if (!roleData.name || !roleData.permissions) {
      return { success: false, message: 'Role name and permissions are required' };
    }

    const payload = { ...roleData };
    // Add user_id if provided (for tracking created_by)
    if (roleData.user_id) {
      payload.user_id = roleData.user_id;
    }

    const { data } = await api.post(ENDPOINTS.CREATE_ROLE, payload);
    
    if (!data || !data.success) {
      return { success: false, message: data?.message || 'Failed to create role' };
    }
    
    return data;
  } catch (error) {
    console.error('Error creating role:', error);
    return { success: false, message: error.response?.data?.message || 'Failed to create role' };
  }
};

/**
 * Update a role
 * @param {number} id - Role ID
 * @param {Object} updateData - Fields to update
 * @returns {Promise<{success: boolean, role: Object, message: string} | null>}
 */
export const updateRole = async (id, updateData) => {
  try {
    const payload = { ...updateData };
    // Add user_id if provided (for tracking updated_by)
    if (updateData.user_id) {
      payload.user_id = updateData.user_id;
    }
    
    const { data } = await api.put(ENDPOINTS.UPDATE_ROLE(id), payload);
    
    if (!data || !data.success) {
      return { success: false, message: data?.message || 'Failed to update role' };
    }
    
    return data;
  } catch (error) {
    console.error('Error updating role:', error);
    return { success: false, message: error.response?.data?.message || 'Failed to update role' };
  }
};

/**
 * Reorder roles
 * @param {Array<{id: number, order: number}>} roleOrders - Array of role IDs with their new order values
 * @param {number} user_id - User ID who is making the update
 * @returns {Promise<{success: boolean, roles: Array, message: string} | null>}
 */
export const reorderRoles = async (roleOrders, user_id = null) => {
  try {
    // Validate required fields
    if (!Array.isArray(roleOrders) || roleOrders.length === 0) {
      return { success: false, message: 'roleOrders must be a non-empty array' };
    }

    const payload = { roleOrders };
    // Add user_id if provided (for tracking updated_by)
    if (user_id) {
      payload.user_id = user_id;
    }

    const { data } = await api.put(ENDPOINTS.REORDER_ROLES, payload);
    
    if (!data || !data.success) {
      return { success: false, message: data?.message || 'Failed to reorder roles' };
    }
    
    return data;
  } catch (error) {
    console.error('Error reordering roles:', error);
    return { success: false, message: error.response?.data?.message || 'Failed to reorder roles' };
  }
};

export default {
  getAllRoles,
  getRoleById,
  createRole,
  updateRole,
  reorderRoles
};
